import React, { FC, useCallback, useEffect, useState } from 'react'
import { cn } from 'shared/utils'
import { Button, Overlay } from '@wmgtech/legato'
import { useReactiveVar } from '@apollo/client'

import { ITag } from 'shared/interfaces'

import { shouldResetAllFiltersVar } from 'modulesCommon/store'
import { ConditionRender } from 'components'
import { FilterSelection } from '..'

import { FilterWrapperProps } from './FilterWrapper.props'
import './FilterWrapper.scss'
import { TFilters, TFilterSelections } from 'shared/types'
import { Tag } from 'shared/__gql-types'

const FilterWrapper: FC<FilterWrapperProps> = ({
  render,
  initialValues,
  defaultValue,
  filterEntities,
  className,
  title = 'Filters',
  total,
  selectionLabels,
  reverseDirection,
  isGlobal = false
}) => {
  const shouldResetAllFilters = useReactiveVar(shouldResetAllFiltersVar)

  const [values, setValues] = useState<TFilters>(initialValues)
  const [isResetClicked, setResetClicked] = useState(false)

  const isFilterSelected = JSON.stringify(values) !== JSON.stringify(defaultValue)

  const resetValues = useCallback(() => {
    if (values !== defaultValue) setValues(defaultValue)
  }, [defaultValue, setValues, values])

  const setUntouched = useCallback(() => {
    setResetClicked(true)
    filterEntities(defaultValue)
    resetValues()
  }, [defaultValue, filterEntities, resetValues])

  const updateValue = useCallback(
    (fieldName: string) => (newValue: string | Tag[]) => {
      if (JSON.stringify(values[fieldName]) !== JSON.stringify(newValue)) {
        setValues({
          ...values,
          [fieldName]: newValue,
        })
      }
    },
    [values]
  )

  const resetValue = useCallback(
    (fieldName: string) => () => {
      setValues({
        ...values,
        [fieldName]: 'all',
        ...(fieldName === 'archived' && { archived: 'hide' })
      })
    },
    [values]
  )

  const resetTagValue = useCallback(
    (tagId: string) => () => {
      setValues({
        ...values,
        tags: values.tags.filter((tag: ITag) => tag.id !== tagId)
      })
    },
    [values]
  )

  useEffect(() => {
    if (JSON.stringify(values) !== JSON.stringify(defaultValue)) {
      setResetClicked(false)
    }
  }, [values, defaultValue])

  useEffect(() => {
    filterEntities(values)
  }, [filterEntities, values])

  useEffect(() => {
    if (shouldResetAllFilters) {
      // setUntouched()
      shouldResetAllFiltersVar(false)
    }
  }, [setUntouched, shouldResetAllFilters])

  return (
    <div className={cn('filter-container', className, { reverse: reverseDirection })}>
      <ConditionRender
        condition={!!total}
        component={
          <FilterSelection
            total={total || ''}
            values={values}
            defaultValue={defaultValue}
            selectionLabels={selectionLabels as TFilterSelections}
            resetValue={resetValue}
            resetTagValue={resetTagValue}
            reverseDirection
          />
        }
      />
      <Overlay
        className="filter-container__popover"
        placement={reverseDirection ? 'bottom-start' : 'bottom-end'}
        trigger={
          <Button
            data-edit-mode
            icon="filter-list"
            colorType={isGlobal ? 'primary' : 'secondary-black'}
            containerStyle={isFilterSelected ? 'solid' : 'outline'}
            className="filter-wrapper__btn-popover"
          />
        }
      >
        <div className="filter-wrapper">
          <h3 className="filter-wrapper__title">
            {title}
            <Button
              label="Clear"
              className="filter-wrapper__btn"
              colorType="secondary-black"
              containerStyle="link"
              size="xs"
              icon="undo"
              onClick={setUntouched}
              disabled={JSON.stringify(values) === JSON.stringify(defaultValue)}
            />
          </h3>
          {render({
            updateValue,
            resetValues,
            shouldBeReset: isResetClicked,
            values
          })}
        </div>
      </Overlay>
    </div>
  )
}

export default FilterWrapper
